import { Injectable } from '@angular/core';
import moment from 'moment';
import { map } from 'rxjs';

import { BusinessPurpose, Ticket } from 'src/app/models/ticket/ticket.model';
import { LoadTickets, LoadTicketsFail, LoadTicketsSuccess } from '../../store';
import { VerticalBarsKpi } from '../types';
import { ChartType } from '../types/chart-type.types';
import { ChartService } from './chart.service';

type VerticalBarsKpiRecord = VerticalBarsKpi['value'][0];
const ALLOWED_BUSINESS_PURPOSES = Object.freeze([
  BusinessPurpose.NocCircuitDown,
  BusinessPurpose.NocCircuitDownBroadband,
  BusinessPurpose.NocCircuitDownDisconnected,
  BusinessPurpose.NocCircuitErrors,
  BusinessPurpose.NocCircuitUpNoSurf,
  BusinessPurpose.NocNetworkOutage,
]);

@Injectable()
export class MTTRCircuitDownChartService extends ChartService<VerticalBarsKpi> {
  public readonly chart$ = this.chartsDataLoader({
    request: LoadTickets,
    failure: LoadTicketsFail,
    success: LoadTicketsSuccess,
  }).pipe(
    map(({ loading, data }) => {
      const dataValue: VerticalBarsKpiRecord[] = Object.values(
        (data?.data || []).reduce(
          (response: Record<string, VerticalBarsKpiRecord>, ticket) => {
            if (!this.filterTickets(ticket)) return response;
            const ticketType = ticket.Portal_Ticket_Type__c;

            (response[ticketType] ??= {
              label: ticketType || '(Other)',
              secondaryValue: 0,
              color: '#3DD8F2',
              value: 0,
            }).value += 1;

            return response;
          },
          {} as Record<string, VerticalBarsKpiRecord>,
        ),
      );

      return {
        loading,
        ngClass: [],
        data: {
          type: ChartType.BARSV,
          id: 'MTTR Circuit Down',
          title: 'Circuit Downtime and Average Repair Time',
          hyperlink: '/tickets',
          value: dataValue,
          yAxisLabel: 'Ticket Count',
        } satisfies VerticalBarsKpi,
      };
    }),
  );

  private filterTickets(ticket: Ticket): boolean {
    if (!ticket.IsClosed) return false;
    if (ticket.ClosedDate == null) return false;

    const dateToCheck = moment(ticket.ClosedDate);
    if (!dateToCheck.isValid()) return false;

    const yearStart = moment().startOf('year');
    const yearEnd = moment().endOf('year');

    const isBetween = dateToCheck.isBetween(yearStart, yearEnd, undefined, '[]');
    if (!isBetween) return false;

    return ALLOWED_BUSINESS_PURPOSES.includes(
      ticket.Business_Purpose__c as BusinessPurpose,
    );
  }
}
